import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Spinner, Alert, Row, Col } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';

export default function ApiTokenCreatePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [error, setError] = useState('');

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    if (!name.trim() || name.trim().length < 2) {
      setError('Token name is required (min 2 characters).');
      return;
    }
    setCreating(true);
    try {
      const res = await adminApi.post('/admin/api-tokens', { name: name.trim(), description: description.trim() });
      setCreated(res.data);
      toast.success('Token created. Copy it now — it will not be shown again.');
    } catch (e) {
      const msg = extractError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }

  function copy(text) {
    navigator.clipboard?.writeText(text)
      .then(() => toast.success('Copied to clipboard'))
      .catch(() => toast.error('Could not copy.'));
  }

  return (
    <>
      <PageHeader
        title="Create API token"
        breadcrumb={<span><Link to="/tokens" className="text-decoration-none">API tokens</Link> / Create</span>}
      />
      <div className="page-content">
        <Row className="g-3">
          <Col xs={12} md={7}>
            <Card>
              <Card.Header>Token details</Card.Header>
              <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {!created ? (
                  <Form onSubmit={onSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., NY Voter Guide WordPress" data-testid="api-token-name-input" />
                      <Form.Text>A label to identify where this token will be used.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control as="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="api-token-description-input" />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button variant="outline-secondary" type="button" onClick={() => navigate('/tokens')}>Cancel</Button>
                      <Button variant="primary" type="submit" disabled={creating} data-testid="api-token-generate-button">
                        {creating ? <Spinner size="sm" animation="border" /> : 'Generate token'}
                      </Button>
                    </div>
                  </Form>
                ) : (
                  <>
                    <Alert variant="warning">
                      <i className="bi bi-exclamation-triangle me-2" />
                      <strong>Copy this token now.</strong> For security, it will not be shown again.
                    </Alert>
                    <div className="mb-3">
                      <Form.Label>Name</Form.Label>
                      <div>{created.name}</div>
                    </div>
                    <div className="mb-3">
                      <Form.Label>Token</Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <code className="token-mono p-2 flex-grow-1" style={{ background: 'var(--app-surface-2)', borderRadius: 8 }} data-testid="api-token-generated-value">
                          {created.token}
                        </code>
                        <Button variant="outline-secondary" onClick={() => copy(created.token)} data-testid="api-token-copy-button">
                          <i className="bi bi-clipboard me-1" /> Copy
                        </Button>
                      </div>
                    </div>
                    <div className="d-flex gap-2 mt-3">
                      <Button variant="primary" onClick={() => navigate('/tokens')}>Done</Button>
                      <Button variant="outline-secondary" onClick={() => { setCreated(null); setName(''); setDescription(''); }}>
                        Create another
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card>
              <Card.Header>How tokens are used</Card.Header>
              <Card.Body style={{ fontSize: '0.9rem' }}>
                <p>External applications include the token as a Bearer header:</p>
                <pre className="token-mono p-2" style={{ background: 'var(--app-surface-2)', borderRadius: 8, fontSize: '0.78rem' }}>{`GET /api/v1/candidates HTTP/1.1
Authorization: Bearer <your-token>`}</pre>
                <ul className="text-muted">
                  <li>Read-only access (no create/update/delete).</li>
                  <li>Returns only candidates with status <em>Published</em>.</li>
                  <li>Internal notes and assignment fields are never exposed.</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}
